const requestResult = (request) => new Promise((resolve, reject) => {
  request.addEventListener("success", () => resolve(request.result), { once: true })
  request.addEventListener("error", () => reject(request.error), { once: true })
})

const transactionComplete = (transaction) => new Promise((resolve, reject) => {
  transaction.addEventListener("complete", () => resolve(), { once: true })
  transaction.addEventListener("abort", () => reject(transaction.error), { once: true })
  transaction.addEventListener("error", () => reject(transaction.error), { once: true })
})

export class TrailerRecordingStore {
  constructor({ indexedDB, databaseName = "veronese-trailer-recordings" }) {
    this.indexedDB = indexedDB
    this.databaseName = databaseName
    this.database = null
    this.databasePromise = null
  }

  async start({ id, mimeType, startedAt, expiresAt }) {
    const database = await this.open()
    const transaction = database.transaction("recordings", "readwrite")
    transaction.objectStore("recordings").put({
      id,
      mimeType,
      startedAt,
      expiresAt,
      state: "recording",
      durationMilliseconds: 0
    })
    await transactionComplete(transaction)
  }

  async appendChunk(recordingId, index, blob, { durationMilliseconds } = {}) {
    const bytes = await blob.arrayBuffer()
    const database = await this.open()
    const transaction = database.transaction([ "recordings", "chunks" ], "readwrite")
    transaction.objectStore("chunks").put({ recordingId, index, bytes })
    if (durationMilliseconds !== undefined) {
      const recordingStore = transaction.objectStore("recordings")
      const request = recordingStore.get(recordingId)
      request.addEventListener("success", () => {
        if (!request.result) return
        recordingStore.put({
          ...request.result,
          durationMilliseconds: Math.max(Number(durationMilliseconds) || 0, 0)
        })
      }, { once: true })
    }
    await transactionComplete(transaction)
  }

  async markReady(recordingId, { durationMilliseconds }) {
    const database = await this.open()
    const transaction = database.transaction("recordings", "readwrite")
    const store = transaction.objectStore("recordings")
    const request = store.get(recordingId)
    request.addEventListener("success", () => {
      if (!request.result) return
      store.put({ ...request.result, state: "ready", durationMilliseconds })
    }, { once: true })
    await transactionComplete(transaction)
  }

  async find(recordingId) {
    const database = await this.open()
    const transaction = database.transaction([ "recordings", "chunks" ], "readonly")
    const metadataPromise = requestResult(transaction.objectStore("recordings").get(recordingId))
    const chunksPromise = requestResult(transaction.objectStore("chunks").index("by_recording").getAll(recordingId))
    const [ metadata, chunks ] = await Promise.all([ metadataPromise, chunksPromise ])
    await transactionComplete(transaction)
    if (!metadata) return null

    const orderedBlobParts = chunks
      .sort((left, right) => left.index - right.index)
      .map((chunk) => chunk.bytes || chunk.blob)
    return {
      ...metadata,
      blob: new Blob(orderedBlobParts, { type: metadata.mimeType })
    }
  }

  async discard(recordingId) {
    const database = await this.open()
    const transaction = database.transaction([ "recordings", "chunks" ], "readwrite")
    transaction.objectStore("recordings").delete(recordingId)
    const chunkStore = transaction.objectStore("chunks")
    const cursorRequest = chunkStore.index("by_recording").openKeyCursor(recordingId)
    cursorRequest.addEventListener("success", () => {
      const cursor = cursorRequest.result
      if (!cursor) return
      chunkStore.delete(cursor.primaryKey)
      cursor.continue()
    })
    await transactionComplete(transaction)
  }

  async deleteExpired(now = Date.now()) {
    const database = await this.open()
    const transaction = database.transaction("recordings", "readonly")
    const recordings = await requestResult(transaction.objectStore("recordings").getAll())
    await transactionComplete(transaction)
    const expiredIds = recordings
      .filter((recording) => Number(recording.expiresAt) <= now)
      .map((recording) => recording.id)

    for (const recordingId of expiredIds) await this.discard(recordingId)
    return expiredIds
  }

  close() {
    if (this.database) {
      this.database.close()
      this.database = null
      this.databasePromise = null
    } else if (this.databasePromise) {
      this.databasePromise.then((database) => database.close()).catch(() => {})
      this.databasePromise = null
    }
  }

  open() {
    if (this.databasePromise) return this.databasePromise

    this.databasePromise = new Promise((resolve, reject) => {
      const request = this.indexedDB.open(this.databaseName, 1)
      request.addEventListener("upgradeneeded", () => {
        const database = request.result
        if (!database.objectStoreNames.contains("recordings")) {
          database.createObjectStore("recordings", { keyPath: "id" })
        }
        if (!database.objectStoreNames.contains("chunks")) {
          const chunkStore = database.createObjectStore("chunks", { keyPath: [ "recordingId", "index" ] })
          chunkStore.createIndex("by_recording", "recordingId")
        }
      })
      request.addEventListener("success", () => {
        this.database = request.result
        resolve(this.database)
      }, { once: true })
      request.addEventListener("error", () => reject(request.error), { once: true })
    })

    return this.databasePromise
  }
}
